import { motion, useInView } from 'motion/react';
import { useRef, useState, useEffect } from 'react';
import { TrendingUp, Users, DollarSign, Zap, Clock } from 'lucide-react';

export function LiveMetrics() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  // Animated counters
  const [metrics, setMetrics] = useState({
    companies: 0,
    savings: 0,
    hoursRecovered: 0,
    roi: 0,
    activeNow: 0,
  });

  useEffect(() => {
    if (!isInView) return;

    const targets = {
      companies: 1247,
      savings: 47000,
      hoursRecovered: 156,
      roi: 847,
      activeNow: 342,
    };

    const duration = 2000; // 2 seconds
    const steps = 60;
    const interval = duration / steps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      
      setMetrics({
        companies: Math.floor(targets.companies * progress),
        savings: Math.floor(targets.savings * progress),
        hoursRecovered: Math.floor(targets.hoursRecovered * progress),
        roi: Math.floor(targets.roi * progress),
        activeNow: Math.floor(targets.activeNow * progress),
      });

      if (step >= steps) {
        clearInterval(timer);
        setMetrics(targets);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [isInView]);

  const metricCards = [
    {
      icon: Users,
      label: 'Empresas Transformadas',
      value: metrics.companies.toLocaleString('pt-BR'),
      suffix: '+',
      color: '#4ECDC4',
      description: 'Em 18 meses',
    },
    {
      icon: DollarSign,
      label: 'Economia Média Mensal',
      value: `R$ ${metrics.savings.toLocaleString('pt-BR')}`,
      suffix: '',
      color: '#10B981',
      description: 'Por empresa',
    },
    {
      icon: Clock,
      label: 'Horas Economizadas',
      value: metrics.hoursRecovered.toLocaleString('pt-BR'),
      suffix: 'h',
      color: '#FFE66D',
      description: 'Mensais por equipe',
    },
    {
      icon: TrendingUp,
      label: 'ROI Médio',
      value: metrics.roi.toLocaleString('pt-BR'),
      suffix: '%',
      color: '#FF6B35',
      description: 'Em 90 dias',
    },
  ];

  return (
    <section ref={ref} className="relative py-24 bg-gradient-to-b from-[#0B0E14] to-[#0A0E1A] overflow-hidden">
      {/* Animated Grid Background */}
      <div className="absolute inset-0 opacity-10">
        <motion.div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(78, 205, 196, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(78, 205, 196, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
          }}
          animate={{
            backgroundPosition: ['0px 0px', '40px 40px'],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#10B981]/30 bg-[#10B981]/10 backdrop-blur-sm mb-6">
            <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
            <span className="text-[#10B981] font-mono text-sm uppercase tracking-wider">
              Dados em Tempo Real
            </span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
            Números Que
            <br />
            <span className="bg-gradient-to-r from-[#10B981] via-[#4ECDC4] to-[#FFE66D] bg-clip-text text-transparent">
              Comprovam Resultados
            </span>
          </h2>

          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Métricas reais de <span className="text-[#4ECDC4]">1.247 empresas</span> que escolheram controle total
          </p>
        </motion.div>

        {/* Main Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {metricCards.map((metric, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="relative group"
            >
              <div className="p-6 rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-transparent backdrop-blur-lg hover:border-white/20 transition-all duration-300">
                {/* Icon */}
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ 
                    background: `linear-gradient(135deg, ${metric.color}20 0%, ${metric.color}05 100%)`,
                    border: `1px solid ${metric.color}30`,
                  }}
                >
                  <metric.icon className="w-6 h-6" style={{ color: metric.color }} />
                </div>

                {/* Value */}
                <div className="mb-2">
                  <motion.span
                    className="text-3xl md:text-4xl font-bold"
                    style={{ color: metric.color }}
                  >
                    {metric.value}
                  </motion.span>
                  <span className="text-2xl font-bold ml-1" style={{ color: metric.color }}>
                    {metric.suffix}
                  </span>
                </div>

                {/* Label */}
                <div className="text-sm text-gray-400 mb-2">
                  {metric.label}
                </div>

                {/* Description */}
                <div className="text-xs text-gray-500 font-mono">
                  {metric.description}
                </div>

                {/* Glow Effect */}
                <div 
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl -z-10"
                  style={{ background: `radial-gradient(circle at center, ${metric.color}30 0%, transparent 70%)` }}
                />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Live Activity Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="max-w-4xl mx-auto"
        >
          <div className="relative p-8 rounded-3xl border border-[#4ECDC4]/30 bg-gradient-to-r from-[#4ECDC4]/10 via-[#0B0E14] to-[#10B981]/10 overflow-hidden">
            {/* Animated Pulse */}
            <motion.div
              className="absolute inset-0"
              animate={{
                background: [
                  'radial-gradient(circle at 0% 50%, rgba(78, 205, 196, 0.1) 0%, transparent 50%)',
                  'radial-gradient(circle at 100% 50%, rgba(16, 185, 129, 0.1) 0%, transparent 50%)',
                  'radial-gradient(circle at 0% 50%, rgba(78, 205, 196, 0.1) 0%, transparent 50%)',
                ],
              }}
              transition={{ duration: 4, repeat: Infinity }}
            />

            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#4ECDC4] to-[#10B981] flex items-center justify-center">
                    <Zap className="w-8 h-8 text-white" />
                  </div>
                  <motion.div
                    className="absolute inset-0 rounded-full bg-[#4ECDC4]"
                    animate={{
                      scale: [1, 1.3, 1],
                      opacity: [0.5, 0, 0.5],
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                    <span className="text-[#10B981] font-mono text-sm">AGORA AO VIVO</span>
                  </div>
                  <p className="text-white text-xl font-semibold">
                    <motion.span
                      key={metrics.activeNow}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-[#4ECDC4]"
                    >
                      {metrics.activeNow}
                    </motion.span>
                    {' '}empresas usando WPTT neste momento
                  </p>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-gradient-to-r from-[#4ECDC4] to-[#10B981] rounded-xl font-semibold text-white shadow-2xl whitespace-nowrap"
              >
                Junte-se a Elas
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Bottom Stats Row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mt-12"
        >
          {[
            { value: '98.7%', label: 'Taxa de Satisfação', color: '#10B981' },
            { value: '4.9/5.0', label: 'Avaliação Média', color: '#FFE66D' },
            { value: '< 24h', label: 'Tempo de Setup', color: '#4ECDC4' },
          ].map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
              className="text-center p-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm"
            >
              <div className="text-3xl font-bold mb-2" style={{ color: stat.color }}>
                {stat.value}
              </div>
              <div className="text-sm text-gray-400">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
